import React, {useContext, useEffect, useState} from "react";
import {Anchor, Box, FileInput, Form, FormField, Image, Select, Text, TextArea, TextInput} from "grommet";
import {useRouter} from "next/router";
import {formatDate, formatDateUTC} from "../../../utils/dates";
import {handleErrors} from "../../../utils/errors";
import {formatCurrency} from "../../../utils/numbers";
import Drawer from "../../Drawer";
import {NotificationsContext} from "../../NotificationsProvider";
import NextAnchor from "../../NextAnchor";
import StatusMessage from "../../StatusMessage";
import HiddenFormField from "../../HiddenFormField";
import Loading from "../../Loading";
import Modal from "../../Modal";
import CurrencyInput from "../../CurrencyInput";
import {isAcceptableFileType} from "../../../utils/files";
import {
    faChevronLeft as IconPrevious,
    faChevronRight as IconNext,
    faFileDownload as IconFileDownload,
    faCircleCheck as IconCheck,
    faSpinnerThird as IconSpinner,
    faTriangleExclamation as IconWarning,
} from '@fortawesome/pro-light-svg-icons';
import MessageBanner from "../../MessageBanner";
import EditableCustomerTag from "../../EditableCustomerTag";
import InvoiceTag from "../../InvoiceTag";
import ReconciliationModal from "./ReconciliationModal";
import FontAwesomeIcon from "../../FontAwesomeIcon";
import {kebabCase} from "../../../utils/string";
import { getTxnTypeDisplayDetails } from "../../../utils/transactions";
import Button from "../../Button";
import { EnhancedButton } from "../../../utils/styled";
import KeyboardShortcut from "../../KeyboardShortcut";
import {
    getTransactionNavigation,
    appendTransactionTokens,
    updateTransactionNavigationMeta
} from "../../../utils/invoiceNavigation";

const UploadAttachmentForm = ({transactionId, callback}) => {
    const notificationCtx = useContext(NotificationsContext);
    const [loading, setLoading] = useState(false);
    const [uploadError, setUploadError] = useState();
    const uploadAttachment = ({ value }) => {
        let failedFiles = [];
        const formData = new FormData();
        formData.append('id', value.id);
        value.txn_files.forEach((file, index) => {
            if (!isAcceptableFileType(file?.name)) {
                failedFiles.push(file?.name);
            } else {
                formData.append(`txn_files[${index}]`, file);
            }
        });
        if (failedFiles.length > 0) {
            setUploadError({
                message: `Unsupported file formats: ${failedFiles.join(", ")}. Please only upload images or PDFs.`
            });
            return;
        }

        setLoading(true);
        fetch(`/api/transactions/files/${value.id}`, {
            method: 'PUT',
            body: formData,
        }).then(handleErrors)
            .then((data) => {
                setLoading(false);
                if (callback) callback(data);
                return data;
            })
            .then(notificationCtx.showNotification)
            .catch(err => {
                notificationCtx.showNotification(err);
                setLoading(false);
            });
    };

    const [filesToUpload, setFilesToUpload] = useState(false);
    const [formError, setFormError] = useState();

    if (loading) return (<Loading />);

    const onChange = (values) => {
        setFilesToUpload(values.txn_files.length > 0);
    }

    return (
        <Form onSubmit={uploadAttachment} onChange={onChange}>
            {uploadError && (<StatusMessage apiError={uploadError} />)}
            {formError && (<StatusMessage apiError={formError} />)}
            <HiddenFormField name="id" value={transactionId} />
            <Box gap="xsmall">
                <FormField
                    component={FileInput}
                    name="txn_files"
                    multiple
                />
                {filesToUpload && <Button event={{section: 'transaction_details_drawer', element: 'upload'}} type="submit" primary label="Upload"/>}
            </Box>
        </Form>
    );
}

const AddMemoForm = ({ transactionId, callback, handleClose }) => {
    const notificationCtx = useContext(NotificationsContext);
    const [formValues, setFormValues] = useState({});
    const [loading, setLoading] = useState(false);
    const addMemo = () => {
        setLoading(true);
        fetch(`/api/transactions/${transactionId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({id: transactionId, memo: {content: formValues.memo_content}}),
        }).then(handleErrors)
            .then((data) => {
                setLoading(false);
                if (callback) callback(data);
                return data;
            })
            .then(notificationCtx.showNotification)
            .catch(err => {
                notificationCtx.showNotification(err);
                setLoading(false);
            });
    };


    if (loading) return (<Loading />);

    const onChange = (values) => {
        setFormValues(({...values}));
    };

    return (
        <Form onSubmit={addMemo} onChange={onChange} value={formValues}>
            <Box>
                <FormField
                    component={TextArea}
                    name="memo_content"
                />
                <Box width="100%" gridArea="buttons" gap="small" direction="row" justify="end">
                    <Button event={{section: 'transaction_details_drawer', element: 'cancel_memo'}} plain onClick={handleClose} secondary label="Cancel" />
                    <Button event={{section: 'transaction_details_drawer', element: 'add_memo'}} type="submit" primary label="Save" onClick={addMemo}/>
                </Box>
            </Box>
        </Form>
    );
}

const RefundModal = ({ transaction, onClose, onSuccess }) => {
    const notificationCtx = useContext(NotificationsContext);

    /*
        Refunds we have to originate ourselves, so we need somewhere to send the money.
        `refund_requires_bank_details` is the backend's answer to that; the type/source
        check behind it is the fallback for payloads that predate the field, and covers
        the same two cases: anything we processed (wire / check / ach_credit received),
        and Stripe bank transfers, because Stripe cannot refund an inbound ACH credit
        back down the path it arrived on. Everything else reverses against the original
        payment method and needs no destination details.
    */
    const isOutboundRefund = transaction?.refund_requires_bank_details ?? (
        transaction?.type === 'Payment'
        || (transaction?.type === 'ExternalPayment' && transaction?.payment_source === 'bank_transfer')
    );
    /*
        One refund per payment is a hard rule server-side, so this is the full amount
        when a refund is still possible and 0 when it is not. It is never a remainder,
        and a partial refund does not leave the rest available.
    */
    const maxAmount = transaction?.max_refund_amount ?? transaction?.amount ?? 0;

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [refundAmount, setRefundAmount] = useState(maxAmount);
    const [memo, setMemo] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [accountType, setAccountType] = useState('checking');
    const [bankName, setBankName] = useState(null);
    const [error, setError] = useState(null);
    const [feeData, setFeeData] = useState(null);
    const [feeLoading, setFeeLoading] = useState(false);

    // Look up bank name when routing number is 9 digits
    useEffect(() => {
        if (!isOutboundRefund || routingNumber.length !== 9) {
            setBankName(null);
            return;
        }

        fetch(`/api/settings/payouts/${routingNumber}`)
            .then(handleErrors)
            .then((data) => setBankName(data?.institution_name || null))
            .catch(() => setBankName(null));
    }, [routingNumber, isOutboundRefund]);

    // Fetch fee when amount changes
    useEffect(() => {
        if (!refundAmount || refundAmount <= 0 || !transaction?.uuid) {
            setFeeData(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            setFeeLoading(true);
            fetch(`/api/transactions/${transaction.uuid}/refund_fee?amount=${refundAmount}`)
                .then(handleErrors)
                .then((data) => {
                    setFeeData(data);
                    setFeeLoading(false);
                })
                .catch(() => {
                    setFeeData(null);
                    setFeeLoading(false);
                });
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [refundAmount, transaction?.uuid]);

    const handleNext = () => {
        if (!refundAmount || refundAmount <= 0) {
            setError({ message: 'Please enter a valid refund amount.' });
            return;
        }
        if (refundAmount > maxAmount) {
            setError({ message: `Refund amount cannot exceed ${formatCurrency(maxAmount, transaction?.currency)}.` });
            return;
        }
        setError(null);
        setStep(2);
    };

    const handleRefund = () => {
        if (isOutboundRefund && step === 2) {
            if (!accountNumber.trim()) {
                setError({ message: 'Please enter the customer\'s bank account number.' });
                return;
            }
            if (!routingNumber.trim()) {
                setError({ message: 'Please enter the bank routing number.' });
                return;
            }
            if (!/^\d{9}$/.test(routingNumber.trim())) {
                setError({ message: 'Routing number must be exactly 9 digits.' });
                return;
            }
        } else {
            if (!refundAmount || refundAmount <= 0) {
                setError({ message: 'Please enter a valid refund amount.' });
                return;
            }
            if (refundAmount > maxAmount) {
                setError({ message: `Refund amount cannot exceed ${formatCurrency(maxAmount, transaction?.currency)}.` });
                return;
            }
        }

        setLoading(true);
        setError(null);

        const refundPayload = {
            amount: refundAmount,
            memo: memo || undefined
        };
        if (isOutboundRefund) {
            refundPayload.account_number = accountNumber.trim();
            refundPayload.routing_number = routingNumber.trim();
            refundPayload.account_type = accountType;
        }

        fetch(`/api/transactions/${transaction.uuid}/refund`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refund: refundPayload }),
        })
            .then(handleErrors)
            .then((data) => {
                setLoading(false);
                notificationCtx.showNotification(data);
                if (onSuccess) onSuccess(data);
                onClose();
            })
            .catch((err) => {
                setLoading(false);
                setError(err);
            });
    };

    return (
        <Modal
            title="Refund payment"
            onClose={onClose}
        >
            <Box gap="small" width="medium">
                {error && <StatusMessage apiError={error} margin="none" />}
                {step === 1 && (
                    <>
                        <Box>
                            <FormField label="Refund amount">
                                <CurrencyInput
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(parseInt(e.target.value) || 0)}
                                    inputCurrency={transaction?.currency}
                                />
                            </FormField>
                            <Text size="xsmall" color="text-weak">
                                Up to {formatCurrency(maxAmount, transaction?.currency)} available
                            </Text>
                        </Box>
                        <FormField label="Memo (optional)">
                            <TextArea
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                placeholder="Reason for refund"
                                resize={false}
                            />
                        </FormField>
                        {feeData && feeData.fee_applies && (
                            <Box background="background-contrast" pad="small" round="xsmall" gap="xsmall" margin={{top: 'small'}}>
                                <Box direction="row" justify="between">
                                    <Text size="small">Refund to customer</Text>
                                    <Text size="small">{formatCurrency(feeData.amount, feeData.currency)}</Text>
                                </Box>
                                <Box direction="row" justify="between">
                                    <Text size="small">Processing fee</Text>
                                    <Text size="small">{formatCurrency(feeData.fee, feeData.currency)}</Text>
                                </Box>
                                <Box direction="row" justify="between" border={{side: 'top', color: 'border'}} pad={{top: 'xsmall'}}>
                                    <Text size="small" weight={600}>Total deducted</Text>
                                    <Text size="small" weight={600}>{formatCurrency(feeData.total_deducted, feeData.currency)}</Text>
                                </Box>
                            </Box>
                        )}
                        {feeLoading && (
                            <Box align="center" pad="xsmall">
                                <Loading plain />
                            </Box>
                        )}
                    </>
                )}
                {step === 2 && (
                    <Box gap="small">
                        <Box background="background-contrast" pad="small" round="xsmall" direction="row" justify="between">
                            <Text size="small">Refund to customer</Text>
                            <Text size="small" weight={600}>{formatCurrency(refundAmount, transaction?.currency)}</Text>
                        </Box>
                        <Text size="small" weight={600}>Customer bank details</Text>
                        {/*
                            Small gap on purpose. The theme already puts 6px above and below
                            each FormField's content, so the parent's "small" gap was stacking
                            on those and spacing every pair of fields twice.
                        */}
                        <Box gap="xsmall">
                            <FormField label="Account number">
                                <TextInput
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                                    placeholder="Bank account number"
                                />
                            </FormField>
                            <Box>
                                <FormField label="Routing number">
                                    <TextInput
                                        value={routingNumber}
                                        onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                                        placeholder="9-digit routing number"
                                        maxLength={9}
                                    />
                                </FormField>
                                {bankName && (
                                    <Text size="small" color="text-weak">{bankName}</Text>
                                )}
                            </Box>
                            <FormField label="Account type">
                                <Select
                                    options={['checking', 'savings']}
                                    value={accountType}
                                    labelKey={(option) => option.charAt(0).toUpperCase() + option.slice(1)}
                                    onChange={({ option }) => setAccountType(option)}
                                />
                            </FormField>
                        </Box>
                    </Box>
                )}
                <Box direction="row" justify="between" align="center" margin={{top: 'small'}}>
                    <Box>
                        {step === 2 && (
                            <Button
                                secondary
                                label="Back"
                                onClick={() => { setStep(1); setError(null); }}
                                disabled={loading}
                            />
                        )}
                    </Box>
                    <Box direction="row" gap="small">
                        <Button
                            secondary
                            label="Cancel"
                            onClick={onClose}
                            disabled={loading}
                        />
                        <Button
                            primary
                            label={loading ? "Processing..." : (isOutboundRefund && step === 1 ? "Next" : "Issue refund")}
                            onClick={isOutboundRefund && step === 1 ? handleNext : handleRefund}
                            disabled={loading}
                            event={{section: 'refund_modal', element: step === 1 && isOutboundRefund ? 'next' : 'submit'}}
                        />
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

const TransactionDetails = ({
    transactionId,
    onClose
}) => {
    const router = useRouter();
    const [data, setData] = useState();
    const [refetch, setRefetch] = useState(false);
    const [clickedAddMemo, setClickedAddMemo] = useState();
    const [showSelectInvoicesModal, setShowSelectInvoicesModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [txnNav, setTxnNav] = useState({ prev: null, next: null, currentIndex: -1, total: 0 });
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const notificationCtx = useContext(NotificationsContext);
    const isNote = () => data?.display_type === "DebitNote" || data?.display_type === "CreditNote";
    const isDebit = () => ['Chargeback', 'Refund'].includes(data?.display_type);

    /*
        The backend owns this decision: `refundable` on transactions/:uuid runs the same
        checks in the same order as the refund POST, so the button and the 422 can no
        longer disagree. Everything below it is the fallback for payloads that predate
        those fields, and goes once they are live in production.
    */
    const isRefundable = () => {
        if (!data) return false;
        if (data.refundable != null) return data.refundable;

        if (data.refunded) return false;
        /*
            A payment someone typed in by hand: we never held that money, so there is
            nothing to send back. Gate on this explicitly rather than by source, because
            a hand-recorded card is indistinguishable from a Stripe-processed one by
            source alone and would otherwise pass the allowlist below.
        */
        if (data.self_recorded) return false;

        // Payment (processed by us): wire, check, ach_credit
        if (data.type === 'Payment') {
            return ['wire', 'check', 'ach_credit'].includes(data.payment_source);
        }

        // ExternalPayment (processed by Stripe): ach_debit, card, bank_transfer
        if (data.type === 'ExternalPayment') {
            return ['ach_debit', 'card', 'bank_transfer'].includes(data.payment_source);
        }

        return false;
    };

    /*
        Of the reasons the backend blocks a refund, settlement is the only one that
        changes on its own. Hiding the button there would read as "we do not support
        this", when the truth is "not yet", so it stays on screen and disabled with the
        backend's own sentence under it. Every other blocked code is permanent for this
        payment and the button simply goes.
    */
    const isRefundPending = () => data?.refundable === false
        && data?.refund_blocked_code === 'payment_not_settled';

    const getRefundBannerConfig = () => {
        if (!data?.refunded && !data?.refund_status) return null;

        const isPartialRefund = data.refund_amount && data.refund_amount < data.amount;
        const refundAmountText = isPartialRefund
            ? formatCurrency(data.refund_amount, data.currency)
            : formatCurrency(data.amount, data.currency);

        const configs = {
            processing: {
                icon: IconSpinner,
                color: 'status-warning',
                header: 'Processing refund',
                message: `${refundAmountText} refund is currently in progress`
            },
            succeeded: {
                icon: IconCheck,
                color: 'status-ok',
                header: isPartialRefund ? 'Partial refund completed' : 'Refund completed',
                message: `${refundAmountText} has been successfully returned to the customer`
            },
            failed: {
                icon: IconWarning,
                color: 'status-critical',
                header: 'Refund unsuccessful',
                message: 'We were unable to complete the refund'
            },
            pending_bank_link: {
                icon: IconWarning,
                color: 'status-warning',
                header: 'Awaiting customer action',
                message: 'Customer must link a bank account to receive the refund'
            }
        };

        if (data.refund_status && configs[data.refund_status]) {
            return configs[data.refund_status];
        }

        // Fallback for refunded=true without specific status
        if (data.refunded) {
            return configs['succeeded'];
        }

        return null;
    };

    // Load navigation from utility
    useEffect(() => {
        const nav = getTransactionNavigation(transactionId);
        setTxnNav(nav);
    }, [transactionId]);

    // Prefetch adjacent transactions for faster navigation
    useEffect(() => {
        if (txnNav.prev) {
            router.prefetch(`/transactions?action=view&transactionId=${txnNav.prev}`);
        }
        if (txnNav.next) {
            router.prefetch(`/transactions?action=view&transactionId=${txnNav.next}`);
        }
    }, [txnNav.prev, txnNav.next, router]);

    // Fetch more transactions when needed
    const fetchMoreTransactions = async () => {
        if (!txnNav.queryUrl || !txnNav.hasMore || isLoadingMore) return null;
        setIsLoadingMore(true);
        try {
            const url = new URL(txnNav.queryUrl, window.location.origin);
            const currentOffset = parseInt(url.searchParams.get('offset') || '0');
            const limit = parseInt(url.searchParams.get('limit') || '20');
            url.searchParams.set('offset', String(currentOffset + limit));

            const response = await fetch(url.toString());
            const data = await handleErrors(response);

            if (data?.transactions?.length > 0) {
                const newIds = data.transactions.map(t => t.id);
                appendTransactionTokens(newIds);
                const newTotal = data.total || txnNav.total;
                const newHasMore = (currentOffset + limit + data.transactions.length) < newTotal;
                updateTransactionNavigationMeta({ hasMore: newHasMore, total: newTotal });
                setTxnNav(getTransactionNavigation(transactionId));
                return newIds[0]; // Return first ID of new page
            }
        } catch (e) {
            console.error('Failed to fetch more transactions:', e);
        } finally {
            setIsLoadingMore(false);
        }
        return null;
    };

    // Navigation handlers
    const handlePrevious = () => {
        if (txnNav.prev) {
            router.push(`/transactions?action=view&transactionId=${txnNav.prev}`, undefined, { scroll: false });
        }
    };

    const handleNext = async () => {
        if (txnNav.next) {
            router.push(`/transactions?action=view&transactionId=${txnNav.next}`, undefined, { scroll: false });
        } else if (txnNav.needsMoreData && !isLoadingMore) {
            const nextId = await fetchMoreTransactions();
            if (nextId) {
                router.push(`/transactions?action=view&transactionId=${nextId}`, undefined, { scroll: false });
            }
        }
    };

    useEffect(() => {
        fetch(`/api/transactions/${transactionId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then(handleErrors)
            .then((data) => setData(data))
            .catch(e => {
                router.back();
                notificationCtx.showNotification({type: 'error', message: 'No transaction details available.'});
            });
    }, [refetch, transactionId]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore if user is typing in an input/textarea
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            if (e.key === 'ArrowLeft' && txnNav.prev) {
                e.preventDefault();
                handlePrevious();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (txnNav.next) {
                    router.push(`/transactions?action=view&transactionId=${txnNav.next}`, undefined, { scroll: false });
                } else if (txnNav.needsMoreData && !isLoadingMore) {
                    fetchMoreTransactions().then(nextId => {
                        if (nextId) {
                            router.push(`/transactions?action=view&transactionId=${nextId}`, undefined, { scroll: false });
                        }
                    });
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [txnNav, isLoadingMore, router]);

    const TxnDetailsField = ({name, value, children}) => (value || children) ? (
        <Box margin={{vertical: 'xsmall'}}>
            <Box>
                <Text weight={600}>{name}</Text>
            </Box>
            {children ? children : (<Box as="span">{value}</Box>) }
        </Box>
    ) : (<></>);

    const txnType = getTxnTypeDisplayDetails(data);
    const Line = ({label, value}) => (
        <Box justify="between" direction="row" align="center" height="30px" gap="small">
            <Text>{label}</Text>
            <Box>
                <Text textAlign="end">{value}</Text>
            </Box>
            
        </Box>
    );

    const previousTransaction = () => (
        <EnhancedButton
            ctaBorderColor={'border'}
            onClick={handlePrevious}
            disabled={!txnNav.prev}
            tip={txnNav.prev ? {
                content: (
                    <Box direction="row" gap="xsmall" align="center">
                        <Text>Previous transaction</Text>
                        <KeyboardShortcut keys="ArrowLeft" />
                    </Box>
                )
            } : 'No previous transaction'}
            styles={`
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            `}
            label={<FontAwesomeIcon SelectedIcon={IconPrevious} size="xsmall" />} />
    )

    const nextTransaction = () => (
        <EnhancedButton
            ctaBorderColor={'border'}
            onClick={handleNext}
            disabled={!txnNav.next && !txnNav.hasMore}
            tip={(txnNav.next || txnNav.hasMore) ? {
                content: (
                    <Box direction="row" gap="xsmall" align="center">
                        <Text>{isLoadingMore ? 'Loading...' : 'Next transaction'}</Text>
                        <KeyboardShortcut keys="ArrowRight" />
                    </Box>
                )
            } : 'No next transaction'}
            styles={`
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
                margin-left: -1px;
            `}
            label={<FontAwesomeIcon SelectedIcon={IconNext} size="xsmall" />} />
    )

    // Only show navigation if we have valid navigation context (transaction is in the list)
    const hasNavigationContext = txnNav.currentIndex !== -1 && txnNav.total > 0;

    return data ? (
        <Drawer
            onClose={onClose}
            leftControl={hasNavigationContext ? (
                <Box direction="row">
                    {previousTransaction()}
                    {nextTransaction()}
                </Box>
            ) : undefined}
            title={txnType.name}
        >
            <Box pad={{horizontal: 'small'}} flex={false} gap="small">
                <Box direction="row" margin={{vertical: 'small'}} gap="small" align="center">
                    <FontAwesomeIcon SelectedIcon={txnType.icon} color="text" size="large" highlight="accent" />
                </Box>
                <Box gap="xsmall" direction="column" pad={{bottom: 'small'}}>
                    {isNote() || data?.self_recorded || data?.display_type.toLowerCase() === 'shopify'  ?
                        (
                            <Line label="Amount" value={formatCurrency(data?.amount, data?.currency)} />
                        ) : isDebit() ? (
                            <>
                                <Line label="Amount" value={formatCurrency(data?.amount, data?.currency)} />
                                <Line label="Fee" value={formatCurrency(data?.fee, data?.currency)} />
                                <Line label="Total amount" value={formatCurrency(data?.net_amount, data?.currency)} />
                            </>
                        ) : (
                            <>
                                <Line label="Gross amount" value={formatCurrency(data?.amount, data?.currency)} />
                                <Line label="Fee" value={formatCurrency(data?.fee, data?.currency)} />
                                <Line label="Net amount" value={formatCurrency(data?.net_amount, data?.currency)} />
                            </>
                        )
                    }
                </Box>
                <Box gap="xsmall" direction="column" pad={{bottom: 'small'}}>
                    {data?.recipient && (
                        <Line
                            label="Customer"
                            value={(
                                <EditableCustomerTag
                                    customerId={data?.recipient_id}
                                    transactionId={data?.id}
                                    editable={!data?.referencable_id}
                                    onSuccess={() => setRefetch(!refetch)}
                                />
                            )}
                        />
                    )}
                    <Line label="Invoice" value={data?.referencable_id ?
                        (
                            <Box pad={{vertical: 'xsmall'}}>
                                <InvoiceTag id={data.referencable_id} />
                            </Box>
                        ) :
                        (
                            <Anchor margin={{top: 'xsmall'}} onClick={()=> setShowSelectInvoicesModal(true)}>+ Add</Anchor>
                        )
                    } />
                    {data.created_at && (<Line label="Transaction date" value={formatDate(data?.created_at)} />)}
                    {(data.payout_id || data.payout_date) && (
                        <Line
                            label="Payout"
                            value={data.payout_id ? (
                                <NextAnchor
                                    href={`/reports?payout_id=${data.payout_id}`}
                                    label={data.payout_date ? formatDateUTC(data.payout_date) : 'View payout'}
                                />
                            ) : formatDateUTC(data.payout_date)}
                        />
                    )}
                </Box>
                {(() => {
                    const refundBanner = getRefundBannerConfig();
                    return refundBanner && (
                        <MessageBanner
                            icon={refundBanner.icon}
                            iconProps={{fixedWidth: true, pad: {top: '5px'}}}
                            color={refundBanner.color}
                            header={refundBanner.header}
                            headerProps={{size: 'medium'}}
                            message={refundBanner.message}
                            containerProps={{pad: 'xsmall', margin: {top: 'small'}}}
                        />
                    );
                })()}
                {isRefundable() && (
                    <Box pad={{bottom: 'small'}}>
                        <Button
                            primary
                            label="Refund"
                            onClick={() => setShowRefundModal(true)}
                            event={{section: 'transaction_details_drawer', element: 'refund'}}
                        />
                    </Box>
                )}
                {isRefundPending() && (
                    <Box pad={{bottom: 'small'}} gap="xsmall">
                        <Button primary disabled label="Refund" />
                        <Text size="small" color="text-weak">{data.refund_blocked_reason}</Text>
                    </Box>
                )}
                <Box border={{side: 'bottom', color: 'border-weak'}} pad={{bottom: 'small'}}>
                    <Text size="small" color="text-weak">{data.recorded_by}</Text>
                </Box>
                <TxnDetailsField name={"Memo"}>
                    <Box>
                        {data?.memo ?
                            (<Text>{data.memo}</Text>) :
                            clickedAddMemo ?
                                (
                                    <AddMemoForm transactionId={data.id} callback={() => setRefetch(!refetch)} handleClose={() => setClickedAddMemo()} />
                                ) : <Anchor margin={{top: 'xsmall'}} onClick={() => setClickedAddMemo(!clickedAddMemo)}>+ Add memo</Anchor>
                        }
                    </Box>
                </TxnDetailsField>
                <TxnDetailsField name={"Attachments"}>
                    <Box margin={{vertical: "xsmall"}} gap="xsmall">
                        <UploadAttachmentForm transactionId={data.id} callback={() => setRefetch(!refetch)} />
                        <Box gap="xsmall">
                            {data.txn_files.map((value, index) => {
                                return <NextAnchor
                                    button
                                    key={index}
                                    target="_blank"
                                    pad={{vertical: "xsmall", horizontal: "xsmall"}}
                                    label={
                                        <Box gap="small">
                                            <Image fit="contain" fill src={value.thumbnail_url}/>
                                            <Box direction="row" gap="xsmall">
                                                <FontAwesomeIcon size="xsmall" SelectedIcon={IconFileDownload} />
                                                <Text weight={400} size="xsmall" truncate={true}>{value.filename}</Text>
                                            </Box>
                                        </Box>
                                    }
                                    href={value.url}
                                    hoverIndicator
                                    secondary
                                    download
                                />
                            })}
                        </Box>
                    </Box>
                </TxnDetailsField>
            </Box>
            {showSelectInvoicesModal && (
                <ReconciliationModal
                    journalEntryType={kebabCase(data?.type)}
                    onClose={() => setShowSelectInvoicesModal(false)}
                    recipientId={data?.recipient_id}
                    transactionId={data.id}
                    onSubmit={() => {
                        setShowSelectInvoicesModal(false);
                        setRefetch(!refetch);
                    }}
                    amountToMatch={data?.amount}
                    singleInvoiceOnly={isNote()}
                />
            )}
            {showRefundModal && (
                <RefundModal
                    transaction={data}
                    onClose={() => setShowRefundModal(false)}
                    onSuccess={() => setRefetch(!refetch)}
                />
            )}
        </Drawer>
    ) : (<></>);
}



export default TransactionDetails;
